import { useCallback, useEffect, useMemo, useState } from 'react';
import { FitbitAvailability, FitbitReadParticipant, readFitbitAvailability } from '../../../services/fitbitCollectionDataService';

// A roster cache is only a hint. Verify against the collection revision before
// presenting its dates as current. At most two background reads run at once;
// selecting a participant shares that read instead of issuing it twice.
export function useParticipantAvailability(studyId: string, revision: number, participants: FitbitReadParticipant[] | null,
  reader: typeof readFitbitAvailability, enabled: boolean) {
  // A new study, collection revision or reader starts a new session, discarding cached reads.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const session = useMemo(() => ({ active: true, requests: new Map<string, Promise<FitbitAvailability>>(), completedAt: new Map<string, number>() }), [studyId, revision, reader]);
  const [results, setResults] = useState<{ session: typeof session; items: Record<string, {
    status: 'checking' | 'ready' | 'error'; data?: FitbitAvailability;
  }> }>({ session, items: {} });
  useEffect(() => { session.active = true; return () => { session.active = false; }; }, [session]);
  const readAvailability = useCallback((requestedStudy: string, participantId: string): Promise<FitbitAvailability> => {
    if (requestedStudy !== studyId) return Promise.reject(new Error('Study changed. Please refresh.'));
    const existing = session.requests.get(participantId);
    const completedAt = session.completedAt.get(participantId);
    if (existing && (completedAt === undefined || Date.now() - completedAt < 60000)) return existing;
    const publish = (item: { status: 'checking' | 'ready' | 'error'; data?: FitbitAvailability }) => {
      if (session.active) setResults(previous => ({ session, items: {
        ...(previous.session === session ? previous.items : {}), [participantId]: item,
      } }));
    };
    publish({ status: 'checking' });
    session.completedAt.delete(participantId);
    const pending = reader(studyId, participantId).then(data => {
      session.completedAt.set(participantId, Date.now()); publish({ status: 'ready', data }); return data;
    }, error => {
      session.requests.delete(participantId); // Retry must make a new request.
      publish({ status: 'error' }); throw error;
    });
    session.requests.set(participantId, pending);
    return pending;
  }, [reader, studyId, session]);
  const ids = JSON.stringify(enabled ? participants?.filter(p => p.enrollmentStatus === 'completed').map(p => p.participantId) || [] : []);
  useEffect(() => {
    let cancelled = false, next = 0;
    const participantIds: string[] = JSON.parse(ids);
    const work = async () => {
      while (!cancelled && next < participantIds.length) {
        const participantId = participantIds[next++];
        try { await readAvailability(studyId, participantId); } catch (_) { /* Individual errors never hide the other participants. */ }
      }
    };
    void work(); void work();
    return () => { cancelled = true; };
  }, [ids, studyId, readAvailability]);
  return { readAvailability, participants: participants?.map(participant => {
    if (!enabled || participant.enrollmentStatus !== 'completed') return participant;
    const result = results.session === session ? results.items[participant.participantId] : undefined;
    return { ...participant, availabilityStatus: result?.status || 'checking' as const,
      dataAvailability: result?.status === 'ready' && result.data
        ? { range: result.data.range, indexedAt: result.data.indexedAt, wear: result.data.coverage?.wear ?? null,
          collection: result.data.collection ?? null } : null };
  }) || null };
}
